import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  CalendarClock,
  FlaskConical,
  Gauge,
  Link2,
  Octagon,
  Pencil,
  Plus,
  SquareCheckBig,
  UserCog,
} from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../app/AuthContext";
import { useGoBack } from "../../shared/hooks/goBack";
import { api } from "../../shared/api/client";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  InlineAlert,
  LoadingState,
  StatusBadge,
  cn,
} from "../../shared/ui";
import type { WorkItemDetail } from "../../shared/types/api";
import { todayLocal } from "../meetings/meetingVocabulary";
import { WorkItemReleasesPanel } from "./WorkItemReleasesPanel";
import { AssignDrawer } from "./AssignDrawer";
import { BlockerDrawer } from "./BlockerDrawer";
import { BugDetailDrawer } from "./BugDetailDrawer";
import { LinkDrawer } from "./LinkDrawer";
import { ReestimateDrawer } from "./ReestimateDrawer";
import { useAnnouncePage } from "../../app/PageTitleContext";
import { ClientServiceDrawer } from "./ClientServiceDrawer";
import { WatchersButton } from "./WatchersButton";
import { SpikeDetailDrawer } from "./SpikeDetailDrawer";
import { RescheduleDrawer } from "../change/RescheduleDrawer";
import { TestingDrawer } from "./TestingDrawer";
import { WorkItemActivity } from "./WorkItemActivity";
import { WorkItemAttachments } from "./WorkItemAttachments";
import { WorkItemBlockers } from "./WorkItemBlockers";
import { WorkItemChildren } from "./WorkItemChildren";
import { WorkItemEvidencePanel } from "./WorkItemEvidencePanel";
import { WorkItemFormDrawer } from "./WorkItemFormDrawer";
import { WorkItemLinks } from "./WorkItemLinks";
import { WorkItemStatusDrawer } from "./WorkItemStatusDrawer";
import { WorkItemTypeLabel } from "./WorkItemTypeBadge";
import { usePublishCurrentProject } from "../../shared/hooks/currentProject";
import {
  PRIORITY_LABELS,
  PRIORITY_TONE,
  REVIEW_LABELS,
  REVIEW_WAIT_IS_LONG,
  SEVERITY_LABELS,
  SEVERITY_TONE,
  STATUS_LABELS,
  STATUS_TONE,
  describePhase,
  formatHours,
  formatSlippage,
  formatVariance,
  hasTestingPhase,
  CONTACT_CHANNEL_LABELS,
  CONTACT_DIRECTION_LABELS,
  needsReview,
  needsRetest,
  waitingForReviewDays,
} from "./workItemVocabulary";

/**
 * Everything recorded about one piece of work (§15–§21, §54–§56, §61).
 *
 * <p>The right-hand rail is built around one idea: what was first planned sits beside what is
 * planned now, on every figure that has both. §55 and §56 keep the originals fixed precisely so
 * this comparison is possible, and a page that showed only the current numbers would waste that.
 */
export function WorkItemDetailPage() {
  const { workItemId = "" } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();

  const [editing, setEditing] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [editingCall, setEditingCall] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [reestimating, setReestimating] = useState(false);
  const [changingDates, setChangingDates] = useState(false);
  const [linking, setLinking] = useState(false);
  const [raisingBlocker, setRaisingBlocker] = useState(false);
  const [editingBug, setEditingBug] = useState(false);
  const [editingSpike, setEditingSpike] = useState(false);
  const [addingChild, setAddingChild] = useState(false);
  const [changingTesting, setChangingTesting] = useState(false);

  const queryClient = useQueryClient();

  const item = useQuery({
    queryKey: ["work-item", workItemId],
    queryFn: () => api.get<WorkItemDetail>(`/api/v1/work-items/${workItemId}`),
  });

  /* Where the back button goes when there is no history to go back to — somebody who followed a
     link in from an email. The list this item lives in: Work, or Bugs for a bug. */
  const listTab = item.data?.workItemType === "BUG" ? "bugs" : "work";
  const { goBack, hasHistory } = useGoBack(
    item.data ? `/projects/${item.data.projectId}?tab=${listTab}` : "/my-work",
  );

  // Tells the shell's create button which project it is standing in.
  usePublishCurrentProject(item.data?.projectId, item.data?.projectName);
  /* Announced from the hook rather than through PageHeader, because this page's heading row
     carries the item's own actions and badges and is not a PageHeader at all. */
  useAnnouncePage(
    item.data?.title ?? "Work item",
    item.data ? `${item.data.itemKey} · ${item.data.projectName}` : undefined,
  );

  /*
   * Everything on this page that can change, invalidated together.
   *
   * The panels each own their query — links, blockers, history and comments load separately so a
   * long conversation does not delay the item itself. The cost is that refetching only the item
   * leaves the rest stale, which shows up as an action that appears to have done nothing. Naming
   * the whole set here keeps that from depending on which panel a drawer happened to be opened
   * from.
   */
  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ["work-item", workItemId] });
    /*
     * Keyed on the item's own id, not on whatever the address bar held. The panels below are
     * given data.id, so once a page can be reached by key as well as by id the two stopped
     * matching — and a refresh that invalidates a key nothing is stored under refreshes nothing.
     */
    const id = item.data?.id ?? workItemId;
    for (const panel of [
      "work-item-links",
      "work-item-blockers",
      "work-item-history",
      "work-item-comments",
    ]) {
      void queryClient.invalidateQueries({ queryKey: [panel, id] });
    }
  };

  if (item.isPending) {
    return <LoadingState label="Loading work item…" />;
  }
  if (item.isError) {
    return (
      <ErrorState error={item.error} onRetry={() => void item.refetch()} />
    );
  }

  const data = item.data;
  const edit = can("WORK_ITEM_EDIT");
  const today = todayLocal();

  return (
    <>
      {/* The title is announced to the bar along the top like every other screen's, so this row
          carries what the item is on the left and what acts on it on the right. On the busiest
          page in the product that is three rows of chrome back, right where the reader is
          trying to read. */}
      <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
        {/* Back to whatever the reader was looking at, with the filters, the page and the tab
            they had set — see useGoBack. Following a link in from outside leaves nothing to go
            back to, so that lands on the list this item lives in: Work, or Bugs for a bug. */}
        <Button
          variant="ghost"
          size="sm"
          icon={<ArrowLeft className="h-4 w-4" />}
          className="-ml-2"
          onClick={goBack}
        >
          {hasHistory ? "Back" : data.projectName}
        </Button>
        {/* The project stays one click away whichever way the button went. */}
        <button
          type="button"
          onClick={() => navigate(`/projects/${data.projectId}?tab=${listTab}`)}
          className="text-xs text-ink-500 hover:text-ink-800 hover:underline"
        >
          {data.projectName}
        </button>
        <span className="font-mono text-xs text-ink-500">{data.itemKey}</span>
        {data.parentKey && (
          <button
            type="button"
            onClick={() => navigate(`/work-items/${data.parentKey}`)}
            className="font-mono text-xs text-brand-600 hover:underline"
            title={data.parentTitle ?? undefined}
          >
            in {data.parentKey}
          </button>
        )}
        <StatusBadge tone={STATUS_TONE[data.status]}>
          {STATUS_LABELS[data.status]}
        </StatusBadge>
        {/* Not while the item is in Development done: the status badge beside this already says
            so, and two badges reading the same words is how a reader learns to stop reading
            them. Once testing starts, this is the only thing still saying development finished —
            and whether it finished on time. */}
        {data.devCompletedDate
          && data.status !== "READY_FOR_QA"
          && data.status !== "DONE"
          && data.status !== "CANCELLED" && (
          <StatusBadge tone={devDoneLate(data) ? "amber" : "green"}>
            Development done{devDoneLate(data) ? " · late" : ""}
          </StatusBadge>
        )}
        <WorkItemTypeLabel type={data.workItemType} />
        {data.bug ? (
          <StatusBadge tone={SEVERITY_TONE[data.bug.severity]}>
            {SEVERITY_LABELS[data.bug.severity]}
          </StatusBadge>
        ) : (
          <StatusBadge tone={PRIORITY_TONE[data.priority]}>
            {PRIORITY_LABELS[data.priority]}
          </StatusBadge>
        )}
        {data.storyPoints != null && (
          <StatusBadge tone="gray">{data.storyPoints} points</StatusBadge>
        )}
        {data.labels.map((label) => (
          <span
            key={label}
            className="rounded-full bg-ink-100 px-2 py-0.5 text-xs text-ink-600"
          >
            {label}
          </span>
        ))}

        <div className="ml-auto flex flex-wrap items-center gap-2">
          {/* Before Edit, because it is a fact about the item rather than something to do to it —
              and because whoever is about to change something should see who will hear about it
              before they reach for the button that does it. */}
          <WatchersButton watchers={data.watchers} />
          {edit && (
            <>
            <Button
              variant="secondary"
              size="sm"
              icon={<Pencil className="h-4 w-4" />}
              onClick={() => setEditing(true)}
            >
              Edit
            </Button>
            {/* Named for what it does, not for where the item is. Labelled with the current
                status it read as a place to go rather than an action, and the status is already
                on the badge directly below — so the one control that moves an item to Done was
                the one thing on the page nobody could find. */}
            <Button
              size="sm"
              icon={<SquareCheckBig className="h-4 w-4" />}
              onClick={() => setChangingStatus(true)}
            >
              Change status
            </Button>
            </>
          )}
        </div>
      </div>

      {/* The dates on a returned item belong to the round that already finished, so they say
          nothing about this one. Said here rather than only at the moment somebody tries to send
          it back to review, which is too late to plan anything. */}
      {(() => {
        if (!needsReview(data)) {
          return null;
        }
        const waiting = waitingForReviewDays(
          { status: "DEV_DONE", devCompletedDate: data.devCompletedDate }, today);
        return (
          <div className="mb-5">
            <InlineAlert tone={(waiting ?? 0) >= REVIEW_WAIT_IS_LONG ? "warning" : "info"}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span>
                  <span className="font-medium">
                    {REVIEW_LABELS[data.reviewState]}
                  </span>
                  {" — "}
                  development finished on {data.devCompletedDate}
                  {waiting != null && waiting > 0 ? `, ${waiting} day${waiting === 1 ? "" : "s"} ago` : ""}
                  {data.reviewerName ? `. With ${data.reviewerName}.` : ". No reviewer named yet."}
                  {data.status === "DONE"
                    ? " This was finished without a review; approving one will ask for it to be"
                      + " tested again."
                    : " It can be tested, and finished, before this is approved — a task"
                      + " finished that way is marked for retesting once it is."}
                </span>

              </div>
            </InlineAlert>
          </div>
        );
      })()}

      {/* The pair of facts that matter: a pass and an approval that describe the same code make a
          finished task, and these two do not. Said here rather than only when a tester next opens
          the item, because the person who needs to know is whoever is looking at it. */}
      {needsRetest(data) && (
        <div className="mb-5">
          <InlineAlert tone="warning">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span>
                <span className="font-medium">Tested before the review</span> — this passed testing
                {data.testingCompletedDate ? ` on ${data.testingCompletedDate}` : ""}, and the
                review was approved afterwards
                {/* Only where it adds something. The two land on the same day often enough that
                    naming both reads as a contradiction rather than a sequence. */}
                {data.reviewApprovedDate
                  && data.reviewApprovedDate !== data.testingCompletedDate
                  ? ` on ${data.reviewApprovedDate}`
                  : ""}
                . The testing describes the code as it was written, not as it was approved, so it
                owes another round.
              </span>
              {edit && (
                <Button
                  size="sm"
                  variant="secondary"
                  icon={<FlaskConical className="h-4 w-4" />}
                  onClick={() => setChangingStatus(true)}
                >
                  Send back for testing
                </Button>
              )}
            </div>
          </InlineAlert>
        </div>
      )}

      {/* One banner for both, because they are one situation: this is a second round of work and
          the figures on the item describe the first. Two warnings stacked would read as two
          problems, and somebody who fixed one would still be looking at a warning. */}
      {(data.awaitingReworkPlan || data.awaitingReworkEstimate) && (
        <div className="mb-5">
          <InlineAlert tone="warning">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span>
                This came back from testing, so the figures on it belong to the round that has
                already finished. It still needs{" "}
                {[
                  data.awaitingReworkEstimate ? "an estimate for this round" : null,
                  data.awaitingReworkPlan ? "a new development due date" : null,
                ]
                  .filter(Boolean)
                  .join(" and ")}
                {" "}before development can be marked done again.
              </span>
              {edit && (
                <span className="flex flex-wrap gap-2">
                  {data.awaitingReworkEstimate && (
                    <Button
                      size="sm"
                      variant="secondary"
                      icon={<Gauge className="h-4 w-4" />}
                      onClick={() => setReestimating(true)}
                    >
                      Re-estimate
                    </Button>
                  )}
                  {data.awaitingReworkPlan && (
                    <Button
                      size="sm"
                      variant="secondary"
                      icon={<CalendarClock className="h-4 w-4" />}
                      onClick={() => setChangingDates(true)}
                    >
                      Set a new due date
                    </Button>
                  )}
                </span>
              )}
            </div>
          </InlineAlert>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Card>
            <CardHeader title="Description" />
            <CardBody>
              {data.description ? (
                <p className="whitespace-pre-wrap text-sm text-ink-700">
                  {data.description}
                </p>
              ) : (
                <p className="text-sm text-ink-400">Nothing written yet.</p>
              )}
              {data.acceptanceCriteria && (
                <div className="mt-4 border-t border-ink-100 pt-4">
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-ink-400">
                    Acceptance criteria
                  </p>
                  <p className="whitespace-pre-wrap text-sm text-ink-700">
                    {data.acceptanceCriteria}
                  </p>
                </div>
              )}
            </CardBody>
          </Card>

          {data.bug && (
            <Card>
              <CardHeader
                title="Defect report"
                subtitle="Severity is how broken it is; priority is when we intend to fix it."
                action={
                  edit && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditingBug(true)}
                    >
                      Edit
                    </Button>
                  )
                }
              />
              <CardBody className="space-y-3">
                <Detail
                  label="Steps to reproduce"
                  value={data.bug.stepsToReproduce}
                />
                <div className="grid gap-3 sm:grid-cols-2">
                  <Detail label="Expected" value={data.bug.expectedResult} />
                  <Detail label="Actual" value={data.bug.actualResult} />
                </div>
                <div className="grid gap-3 border-t border-ink-100 pt-3 sm:grid-cols-3">
                  <Small label="Found in" value={data.bug.environment} />
                  <Small
                    label="Affected version"
                    value={data.bug.affectedVersion}
                  />
                  <Small label="Fix version" value={data.bug.fixVersion} />
                </div>
                {(data.bug.rootCause || data.bug.resolution) && (
                  <div className="space-y-3 border-t border-ink-100 pt-3">
                    <Detail label="Root cause" value={data.bug.rootCause} />
                    <Detail label="Resolution" value={data.bug.resolution} />
                  </div>
                )}
              </CardBody>
            </Card>
          )}

          {data.spike && (
            <Card>
              <CardHeader
                title="Investigation"
                subtitle={
                  data.spike.answered
                    ? "Answered."
                    : "Unanswered — a spike whose conclusion is never written down has to be run again."
                }
                action={
                  edit && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditingSpike(true)}
                    >
                      Edit
                    </Button>
                  )
                }
              />
              <CardBody className="space-y-3">
                <Detail label="Question" value={data.spike.question} />
                <Detail label="Objective" value={data.spike.objective} />
                <div className="grid gap-3 sm:grid-cols-2">
                  <Small
                    label="Time box"
                    value={
                      data.spike.timeBoxHours != null
                        ? formatHours(data.spike.timeBoxHours)
                        : null
                    }
                  />
                  <Small
                    label="Expected output"
                    value={data.spike.expectedOutput}
                  />
                </div>
                {(data.spike.conclusion || data.spike.recommendation) && (
                  <div className="space-y-3 border-t border-ink-100 pt-3">
                    <Detail label="Conclusion" value={data.spike.conclusion} />
                    <Detail
                      label="Recommendation"
                      value={data.spike.recommendation}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          )}

          {data.clientService && (
            <Card>
              <CardHeader
                title="The call"
                subtitle={
                  data.clientService.recorded
                    ? "Recorded. This is what the next person who speaks to them will read."
                    : "Not recorded yet — the item cannot be completed until it is."
                }
                action={
                  edit && (
                    <Button variant="ghost" size="sm" onClick={() => setEditingCall(true)}>
                      {data.clientService.recorded ? "Edit" : "Record the call"}
                    </Button>
                  )
                }
              />
              <CardBody className="space-y-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <Small
                    label="Who called whom"
                    value={
                      data.clientService.direction
                        ? CONTACT_DIRECTION_LABELS[data.clientService.direction]
                        : null
                    }
                  />
                  <Small label="Who you spoke to" value={data.clientService.contactName} />
                  <Small label="Client" value={data.clientService.clientName} />
                  <Small
                    label="How"
                    value={
                      data.clientService.channel
                        ? CONTACT_CHANNEL_LABELS[data.clientService.channel]
                        : null
                    }
                  />
                </div>
                <Detail
                  label="What you explained"
                  value={data.clientService.whatWasExplained}
                />
                {data.clientService.missing.length > 0 && (
                  <InlineAlert tone="warning">
                    Still needed before this can be completed:{" "}
                    {data.clientService.missing.join(", ")}.
                  </InlineAlert>
                )}
              </CardBody>
            </Card>
          )}

          {/*
            * Keyed on the item so the panel remounts when the route moves to a different one.
            * The route renders a single element, so without this React reuses the instance and
            * the panel's useState initialisers never run again: opening migration A and then
            * migration B left A's queries in B's box, and Save wrote them onto B's record.
            */}
          <WorkItemEvidencePanel key={data.id} item={data} onChanged={reload} />

          <WorkItemChildren
            item={data}
            canEdit={can("WORK_ITEM_CREATE")}
            onAdd={() => setAddingChild(true)}
          />

          <WorkItemLinks
            workItemId={data.id}
            canEdit={edit}
            onAdd={() => setLinking(true)}
            onChanged={reload}
          />

          <WorkItemBlockers
            workItemId={data.id}
            canEdit={edit}
            onAdd={() => setRaisingBlocker(true)}
            onChanged={reload}
          />

          <WorkItemAttachments workItemId={data.id} />

          <WorkItemActivity workItemId={data.id} />
        </div>

        {/* The right rail: who, how much, and when — each with what was first planned beside it. */}
        <div className="space-y-4">
          <WorkItemReleasesPanel item={data} onChanged={reload} />
          <Card>
            <CardHeader
              title="People"
              action={
                can("WORK_ITEM_ASSIGN") && (
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<UserCog className="h-4 w-4" />}
                    onClick={() => setAssigning(true)}
                  >
                    Change
                  </Button>
                )
              }
            />
            <CardBody className="space-y-3">
              <Row
                label="Assignee"
                value={data.assigneeName ?? "Unassigned"}
                muted={!data.assigneeName}
              />
              <Row
                label="Reporter"
                value={data.reporterName ?? "—"}
                muted={!data.reporterName}
              />
              <Row
                label="Team"
                value={data.teamName ?? "—"}
                muted={!data.teamName}
              />
              {/* Shown only when set. A row reading "—" on every item of a single-module product
                  is a fact about the product, not about this item. */}
              {data.moduleName && (
                <Row label="Module" value={data.moduleName} />
              )}
              {data.contributors.length > 0 && (
                <div className="border-t border-ink-100 pt-3">
                  <p className="mb-1.5 text-sm text-ink-500">Contributors</p>
                  <div className="flex flex-wrap gap-1.5">
                    {data.contributors.map((person) => (
                      <span
                        key={person.id}
                        className="rounded-full bg-ink-100 px-2 py-0.5 text-xs text-ink-700"
                      >
                        {person.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Effort"
              subtitle="First estimated, against now."
              action={
                edit && (
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<Gauge className="h-4 w-4" />}
                    onClick={() => setReestimating(true)}
                  >
                    {data.originalEstimateHours == null ? "Estimate" : "Re-estimate"}
                  </Button>
                )
              }
            />
            <CardBody className="space-y-3">
              <Row
                label="Originally"
                value={formatHours(data.originalEstimateHours)}
              />
              <Row label="Now" value={formatHours(data.currentEstimateHours)} />
              <Row
                label="Remaining"
                value={formatHours(data.remainingEstimateHours)}
              />
              <Row label="Logged" value={formatHours(data.actualLoggedHours)} />
              {/*
               * Two variances, because they answer two questions and disagree constantly — and
               * both are right. A task estimated at 10h, re-estimated to 20h, with 2h left and
               * nothing logged has grown by 10h and is forecast to come in 8h under. One number
               * called "Variance" showed only the second, so a doubled estimate was reported in
               * green as though it had come in under budget.
               */}
              <div className="space-y-3 border-t border-ink-100 pt-3">
                <Row
                  label="Estimate moved"
                  value={formatVariance(data.estimateGrowthHours)}
                  tone={
                    !data.estimateGrowthHours
                      ? undefined
                      : data.estimateGrowthHours > 0
                        ? "bad"
                        : "good"
                  }
                />
                <Row
                  label="Expected total"
                  value={formatHours(data.estimateAtCompletionHours)}
                />
                <Row
                  label="Against the original"
                  value={formatVariance(data.effortVarianceHours)}
                  tone={
                    !data.effortVarianceHours
                      ? undefined
                      : data.effortVarianceHours > 0
                        ? "bad"
                        : "good"
                  }
                />
              </div>
              <p className="text-xs text-ink-400">
                <span className="font-medium">Estimate moved</span> is how far
                the estimate itself has travelled since it was first made.{" "}
                <span className="font-medium">Expected total</span> is what is
                logged plus what is left, and{" "}
                <span className="font-medium">against the original</span>{" "}
                compares that to what was first said.
              </p>
              <p className="text-xs text-ink-400">
                Logged hours are the time recorded against this item on people's
                timesheets — the Worklogs tab below shows whose. Zero means
                nothing has been logged, not that nothing was spent.
              </p>
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Dates"
              subtitle="Four of each, never collapsed."
              action={
                edit && (
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<CalendarClock className="h-4 w-4" />}
                    onClick={() => setChangingDates(true)}
                  >
                    Change
                  </Button>
                )
              }
            />
            <CardBody className="space-y-3">
              {hasTestingPhase(data.workItemType) && (
                <div className="space-y-3 border-b border-ink-100 pb-3">
                  <PhaseRow
                    label="Development"
                    phase={describePhase(
                      data.committedDueDate ?? data.originalDueDate,
                      data.devCompletedDate,
                      today,
                    )}
                  />
                  <PhaseRow
                    label="Testing"
                    phase={describePhase(data.testingDueDate, data.testingCompletedDate, today)}
                    prefix={data.testerName ?? "No tester yet"}
                    suffix={
                      data.qaReturnCount > 0
                        ? `${data.qaReturnCount} QA return${data.qaReturnCount === 1 ? "" : "s"}`
                        : undefined
                    }
                  />
                  {edit && (
                    <div className="flex justify-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setChangingTesting(true)}
                      >
                        Testing → Change
                      </Button>
                    </div>
                  )}
                </div>
              )}
              <Row label="Originally due" value={data.originalDueDate ?? "—"} />
              <Row label="Committed" value={data.committedDueDate ?? "—"} />
              <Row label="Forecast" value={data.forecastDueDate ?? "—"} />
              <Row label="Completed" value={data.actualCompletedDate ?? "—"} />
              <div className="space-y-3 border-t border-ink-100 pt-3">
                <Row
                  label="Moved since first planned"
                  value={
                    data.originalDueDate
                      ? formatSlippage(data.dueDateDriftDays)
                      : "not measurable"
                  }
                  tone={data.dueDateDriftDays > 0 ? "bad" : undefined}
                />
                <Row
                  label="Against the commitment"
                  value={
                    data.committedDueDate
                      ? formatSlippage(data.scheduleVarianceDays)
                      : "not measurable"
                  }
                  tone={
                    !data.committedDueDate
                      ? undefined
                      : data.scheduleVarianceDays > 0
                        ? "bad"
                        : "good"
                  }
                />
              </div>
            </CardBody>
          </Card>

          {edit && (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                size="sm"
                icon={<Link2 className="h-4 w-4" />}
                onClick={() => setLinking(true)}
              >
                Link
              </Button>
              <Button
                variant="secondary"
                size="sm"
                icon={<Octagon className="h-4 w-4" />}
                onClick={() => setRaisingBlocker(true)}
              >
                Blocker
              </Button>
              {data.workItemType !== "SUBTASK" && (
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<Plus className="h-4 w-4" />}
                  onClick={() => setAddingChild(true)}
                >
                  Child
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      <WorkItemFormDrawer
        projectId={data.projectId}
        item={data}
        open={editing}
        onClose={() => setEditing(false)}
        onSaved={reload}
      />
      <WorkItemFormDrawer
        projectId={data.projectId}
        parentId={data.id}
        parentLabel={`${data.itemKey} — ${data.title}`}
        open={addingChild}
        onClose={() => setAddingChild(false)}
        onSaved={reload}
      />
      {data.clientService && (
        <ClientServiceDrawer
          item={data}
          open={editingCall}
          onClose={() => setEditingCall(false)}
          onSaved={reload}
        />
      )}
      <WorkItemStatusDrawer
        item={changingStatus ? data : null}
        onClose={() => setChangingStatus(false)}
        onSaved={reload}
        onSetUpTesting={
          edit
            ? () => {
                setChangingStatus(false);
                setChangingTesting(true);
              }
            : undefined
        }
        onReplan={
          edit
            ? () => {
                setChangingStatus(false);
                setChangingDates(true);
              }
            : undefined
        }
      />
      <AssignDrawer
        item={assigning ? data : null}
        onClose={() => setAssigning(false)}
        onSaved={reload}
      />
      <ReestimateDrawer
        item={reestimating ? data : null}
        onClose={() => setReestimating(false)}
        onSaved={reload}
      />
      <RescheduleDrawer
        item={changingDates ? data : null}
        onClose={() => setChangingDates(false)}
        onSaved={reload}
      />
      <TestingDrawer
        item={changingTesting ? data : null}
        onClose={() => setChangingTesting(false)}
        onSaved={reload}
      />
      <LinkDrawer
        item={linking ? data : null}
        onClose={() => setLinking(false)}
        onSaved={reload}
      />
      <BlockerDrawer
        item={raisingBlocker ? data : null}
        onClose={() => setRaisingBlocker(false)}
        onSaved={reload}
      />
      <BugDetailDrawer
        item={editingBug ? data : null}
        onClose={() => setEditingBug(false)}
        onSaved={reload}
      />
      <SpikeDetailDrawer
        item={editingSpike ? data : null}
        onClose={() => setEditingSpike(false)}
        onSaved={reload}
      />
    </>
  );
}

/**
 * Whether development finished after its deadline — the header badge turns amber.
 *
 * <p>Committed over original, matching the server's own {@code DeliveryMetrics.resolveDeadline}:
 * an item with no committed date yet still has an original one to be judged against.
 */
function devDoneLate(data: WorkItemDetail): boolean {
  const deadline = data.committedDueDate ?? data.originalDueDate;
  return Boolean(data.devCompletedDate && deadline && data.devCompletedDate > deadline);
}

/**
 * A development or testing phase line: an optional prefix (the tester's name), the phase's own
 * words from {@link describePhase}, and an optional suffix (the QA return count) — coloured by
 * the phase's tone rather than the fixed good/bad two Row offers, since a phase can also just be
 * "calm" and in progress.
 */
function PhaseRow({
  label,
  phase,
  prefix,
  suffix,
}: {
  label: string;
  phase: { text: string; tone: "calm" | "good" | "late" };
  prefix?: string;
  suffix?: string;
}) {
  const text = [prefix, phase.text, suffix].filter(Boolean).join(" · ");
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-ink-500">{label}</span>
      <span
        className={cn(
          "tnum text-right",
          phase.tone === "late"
            ? "font-medium text-red-600"
            : phase.tone === "good"
              ? "text-emerald-600"
              : "text-ink-900",
        )}
      >
        {text}
      </span>
    </div>
  );
}

function Row({
  label,
  value,
  muted,
  tone,
}: {
  label: string;
  value: string;
  muted?: boolean;
  tone?: "good" | "bad";
}) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-ink-500">{label}</span>
      <span
        className={cn(
          "tnum text-right",
          muted
            ? "text-ink-400"
            : tone === "bad"
              ? "font-medium text-red-600"
              : tone === "good"
                ? "text-emerald-600"
                : "text-ink-900",
        )}
      >
        {value}
      </span>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string | null }) {
  if (!value) {
    return null;
  }
  return (
    <div>
      <p className="mb-0.5 text-xs font-semibold uppercase tracking-wider text-ink-400">
        {label}
      </p>
      <p className="whitespace-pre-wrap text-sm text-ink-700">{value}</p>
    </div>
  );
}

function Small({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <p className="text-xs text-ink-500">{label}</p>
      <p
        className={cn(
          "mt-0.5 text-sm",
          value ? "text-ink-900" : "text-ink-400",
        )}
      >
        {value ?? "—"}
      </p>
    </div>
  );
}
